// src/components/DocumentList.js
// Updated: 10/31/2025 09:28 PM EDT (null-safe helpers; guidance session label; minor resiliency)
// --- THIS IS THE FULL, UPDATED FILE ---

import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, FileImage, Award, Shield } from 'lucide-react';

// Null-safe Firestore Timestamp formatter
const formatDate = (timestamp) => {
  const d = timestamp?.toDate?.();
  if (!d) return '...';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Prefer doc.title; otherwise strip extension from fileName; fallback to "Untitled"
const getDisplayTitle = (doc) => {
  const t = (doc?.title || '').trim();
  if (t) return t;
  const f = (doc?.fileName || '').trim();
  return f ? f.replace(/\.[^/.]+$/, '') : 'Untitled';
};

// Choose an icon based on context/file type
const DocIcon = ({ doc }) => {
  if (doc?.context === 'scoring') return <Award className="h-10 w-10 text-yellow-500" />;
  if (doc?.context === 'guidance') return <FileText className="h-10 w-10 text-indigo-500" />;
  if (doc?.fileType === 'application/pdf') return <FileText className="h-10 w-10 text-red-500" />;
  return <FileImage className="h-10 w-10 text-blue-500" />;
};

export default function DocumentList({ documents }) {
  if (!Array.isArray(documents) || documents.length === 0) {
    return (
      <div className="text-center text-gray-500">
        <p>No documents found.</p>
        <p>Click "Upload Document" to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      <ul className="divide-y divide-gray-200">
        {documents.map((doc) => {
          const uploaderName = doc?.uploaderName || 'Unknown';
          const uploadedAtStr = formatDate(doc?.uploadedAt);
          const isGuidance = doc?.context === 'guidance';
          const isScoring = doc?.context === 'scoring';
          const isPublic = !!doc?.isPublic;
          const linkedSessionName =
            (doc?.linkedSessionName && String(doc.linkedSessionName)) || 'Unknown Session';

          return (
            <li key={doc.id}>
              <Link to={`/documents/${doc.id}`} className="block hover:bg-gray-50">
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="flex min-w-0 flex-1 items-center">
                    <div className="flex-shrink-0">
                      <DocIcon doc={doc} />
                    </div>

                    <div className="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                      {/* Left column: title + subtext */}
                      <div className="min-w-0">
                        <p className="truncate text-lg font-medium text-blue-600">
                          {getDisplayTitle(doc)}
                        </p>

                        {/* Subtext / context line */}
                        {isScoring ? (
                          <p
                            className="mt-1 flex items-center text-sm font-medium text-green-700"
                            title={linkedSessionName}
                          >
                            {doc?.linkedPackageName || 'Scoring Package'}
                            <span className="ml-2 hidden text-xs font-normal text-gray-500 sm:block">
                              (Session: {linkedSessionName})
                            </span>
                          </p>
                        ) : isGuidance ? (
                          <p
                            className="mt-1 flex items-center text-sm font-medium text-indigo-700"
                            title={`Session: ${linkedSessionName}`}
                          >
                            <Shield className="mr-1.5 h-4 w-4 text-indigo-500" />
                            Guidance for: {linkedSessionName}
                          </p>
                        ) : isPublic ? (
                          <p
                            className="mt-1 flex items-center text-sm text-gray-500"
                            title="Visible to all organization members"
                          >
                            <Shield className="mr-1.5 h-4 w-4 text-gray-400" />
                            Public Document
                          </p>
                        ) : (
                          <p className="mt-1 flex items-center text-sm text-gray-500">
                            Uploaded by: {uploaderName}
                          </p>
                        )}
                      </div>

                      {/* Right column: uploader / date */}
                      <div className="hidden md:block text-right">
                        {(isScoring || isGuidance || isPublic) ? (
                          <p className="text-sm text-gray-900">
                            Uploaded by: {uploaderName}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-900">Uploaded on: {uploadedAtStr}</p>
                        )}
                        {/* Always show date as a subtle secondary line */}
                        <p className="mt-1 text-xs text-gray-500">Date: {uploadedAtStr}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400">&gt;</span>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
